/**
 * Data-layer FACADE. Every surface imports its API functions from here; this module picks the LIVE
 * `/api` client or the in-memory MOCKS based on config.USE_MOCKS (VITE_USE_MOCKS / Vitest). This is
 * the single seam where "wire to the real backend" happens — consumers are unchanged whether the
 * backend is up or not.
 *
 * Signatures match the original mock functions so the swap is transparent. Live mutations that the
 * mock returned an updated entity for now return void + the caller re-fetches (the API returns only
 * an ack), so mutation helpers here re-GET the request to keep the console in sync.
 */
import { USE_MOCKS } from "./config.js";
import type { RequestStatus } from "@td/shared";
import type {
  AddSubscriberByStaffInput,
  AdvanceCaseInput,
  AdvancePermitInput,
  AnalyticsView,
  AttachmentKind,
  BroadcastDetail,
  BroadcastRow,
  BulkItemResult,
  CaseQueueRow,
  CategoryKind,
  CategoryView,
  ChannelView,
  ContractorDetailView,
  ContractorQueueRow,
  ContractorView,
  CouncilReportResult,
  CouncilReportScope,
  CreateBroadcastInput,
  CreatePermitByStaffInput,
  CreatePermitByStaffResult,
  DeliveryMethod,
  FAQAnalytics,
  FAQView,
  GroupView,
  KORAQueueRow,
  MailingAddress,
  NotificationTemplateView,
  NotifyLandingResult,
  OcrPermitFieldsInput,
  OcrPermitFieldsResult,
  OpenCaseInput,
  OpenCaseResult,
  PermissionsRegistryModule,
  PermitQueueRow,
  PreferencesResult,
  PreferencesUpdateResult,
  PublicContractorView,
  PublishFAQInput,
  PutContractorInput,
  QuestionQueueRow,
  QueueRow,
  CoachingState,
  CoachingStatePatch,
  RecordInspectionInput,
  RenewContractorInput,
  RespondKORAInput,
  SavedView,
  SavedViewFilters,
  SendBroadcastResult,
  StaffCaseDetail,
  StaffKORADetail,
  StaffPermitDetail,
  StaffQuestionDetail,
  StaffRequestDetail,
  StaffUserView,
  SubmitKORAInput,
  SubmitPermitInput,
  SubscribeResult,
  SubscriberView,
  SubscriptionStatus,
  SuggestFAQDraft,
  UpdateFAQInput,
  Visibility,
} from "./types.js";
import type { PublicTracking } from "./trackingTypes.js";
import type { SubmitReportInput } from "@td/shared";
import type {
  CreateRequestInput,
  CreateRequestResult,
  BrandingSettingsView,
  BrandingCrawlResult,
  InviteStaffResult,
  ResendOutcome,
} from "./staffApi.js";
import {
  getPermissionsRegistryLive,
  listGroupsLive,
  createGroupLive,
  updateGroupLive,
  deleteGroupLive,
  setStaffGroupsLive,
  getMyPermissionsLive,
} from "./staffApi.js";
import type { SubmitQuestionInput } from "./publicApi.js";
import type { AiCleanupMode } from "./types.js";

// ---- Mock implementations --------------------------------------------------
import * as mockDb from "./mockDb.js";
import {
  submitReport as mockSubmit,
  uploadAttachment as mockUpload,
  reverseGeocode as mockReverseGeocode,
} from "./mockApi.js";
import {
  submitQuestion as mockSubmitQuestion,
  listQuestionQueue as mockListQuestionQueue,
  getQuestion as mockGetQuestion,
  answerQuestion as mockAnswerQuestion,
  closeQuestion as mockCloseQuestion,
  assignQuestion as mockAssignQuestion,
  browseFAQ as mockBrowseFAQ,
  searchFAQ as mockSearchFAQ,
  getFAQ as mockGetFAQ,
  voteFAQ as mockVoteFAQ,
  listAdminFAQ as mockListAdminFAQ,
  publishFAQ as mockPublishFAQ,
  updateFAQ as mockUpdateFAQ,
  suggestFAQFromConcern as mockSuggestFAQFromConcern,
  getFAQAnalytics as mockGetFAQAnalytics,
  submitKORA as mockSubmitKORA,
  listKORAQueue as mockListKORAQueue,
  getKORA as mockGetKORA,
  respondKORA as mockRespondKORA,
  assignKORA as mockAssignKORA,
  addKORANote as mockAddKORANote,
  addKORAAttachment as mockAddKORAAttachment,
  listKORARecordTypes as mockListKORARecordTypes,
  openCase as mockOpenCase,
  listCases as mockListCases,
  getCase as mockGetCase,
  advanceCase as mockAdvanceCase,
  assignCase as mockAssignCase,
  addCaseNote as mockAddCaseNote,
  addCaseAttachment as mockAddCaseAttachment,
  listCaseViolationTypes as mockListCaseViolationTypes,
  submitPermit as mockSubmitPermit,
  listPermits as mockListPermits,
  getPermit as mockGetPermit,
  advancePermit as mockAdvancePermit,
  recordPermitInspection as mockRecordPermitInspection,
  assignPermit as mockAssignPermit,
  addPermitNote as mockAddPermitNote,
  addPermitAttachment as mockAddPermitAttachment,
  listPermitTypes as mockListPermitTypes,
  ocrPermitFields as mockOcrPermitFields,
  createPermitByStaff as mockCreatePermitByStaff,
  addSubscriberByStaff as mockAddSubscriberByStaff,
  listContractors as mockListContractors,
  getContractor as mockGetContractor,
  putContractor as mockPutContractor,
  renewContractor as mockRenewContractor,
  addContractorNote as mockAddContractorNote,
  listTradeCategories as mockListTradeCategories,
  listPublicContractors as mockListPublicContractors,
  getPublicContractor as mockGetPublicContractor,
  listBGShareCandidates as mockListBGShareCandidates,
  saveBGShareApproval as mockSaveBGShareApproval,
  unapproveBGShare as mockUnapproveBGShare,
} from "./mockDb.js";

import { cleanupAnswerMock } from "./aiCleanup.js";
export { AI_NOT_CONFIGURED } from "./aiCleanup.js";

// ---- Live implementations --------------------------------------------------
import {
  submitReportLive,
  uploadAttachmentLive,
  getPublicTrackingLive,
  addResidentCommentLive,
  submitSatisfactionSurveyLive,
  subscribeLive,
  resendConfirmationLive,
  manageLinkLive,
  confirmSubscriptionLive,
  unsubscribeLive,
  getPreferencesLive,
  updatePreferencesLive,
  reverseGeocodeLive,
  submitQuestionLive,
  browseFAQLive,
  searchFAQLive,
  getFAQLive,
  voteFAQLive,
  submitKORALive,
  submitPermitLive,
  type SubmitReportResult,
} from "./publicApi.js";
import {
  createRequestLive,
  listQueueLive,
  getRequestLive,
  changeStatusLive,
  assignRequestLive,
  addNoteLive,
  addStaffAttachmentLive,
  listCategoriesLive,
  saveCategoriesLive,
  listQuestionTopicsLive,
  listStaffLive,
  inviteStaffLive,
  resendStaffInviteLive,
  setStaffActiveLive,
  updateStaffLive,
  getBrandingLive,
  saveBrandingLive,
  crawlBrandingLive,
  uploadLogoLive,
  uploadDocumentLive,
  listTemplatesLive,
  saveTemplateLive,
  listBroadcastsLive,
  getBroadcastLive,
  saveBroadcastDraftLive,
  sendBroadcastLive,
  sendBroadcastTestLive,
  getAnalyticsLive,
  listSavedViewsLive,
  saveSavedViewLive,
  deleteSavedViewLive,
  getCoachingStateLive,
  putCoachingStateLive,
  deleteCoachingStateLive,
  bulkAssignLive,
  bulkChangeStatusLive,
  listQuestionQueueLive,
  getQuestionLive,
  answerQuestionLive,
  closeQuestionLive,
  listAdminFAQLive,
  publishFAQLive,
  updateFAQLive,
  suggestFAQFromConcernLive,
  getFAQAnalyticsLive,
  listKORAQueueLive,
  getKORALive,
  respondKORALive,
  listKORARecordTypesLive,
  openCaseLive,
  listCasesLive,
  getCaseLive,
  advanceCaseLive,
  listCaseViolationTypesLive,
  listPermitsLive,
  getPermitLive,
  advancePermitLive,
  recordInspectionLive,
  listPermitTypesLive,
  ocrPermitFieldsLive,
  createPermitByStaffLive,
  addSubscriberByStaffLive,
  listContractorsLive,
  getContractorLive,
  putContractorLive,
  renewContractorLive,
  addContractorNoteLive,
  listTradeCategoriesLive,
  getCouncilReportLive,
  getCouncilReportPdfLive,
  exportMailingListCsvLive,
  listSubscribersLive,
  unsubscribeSubscriberLive,
  resendSubscriberConfirmationLive,
  listStaffConcernsLive,
  listBGShareCandidatesLive,
  saveBGShareApprovalLive,
  unapproveBGShareLive,
} from "./staffApi.js";
import { cleanupAnswerLive } from "./aiCleanup.js";
import {
  listPublicContractorsLive,
  getPublicContractorLive,
  listPublicTradeCategoriesLive,
  type PublicTradeCategory,
} from "./contractors.js";

export { ApiError } from "./client.js";
export type { SubmitReportResult } from "./publicApi.js";
export type { PublicTracking } from "./trackingTypes.js";
export type {
  SavedView,
  SavedViewFilters,
  BulkItemResult,
  QueueRow,
  StaffUserView,
  SubscriberView,
  SubscriptionStatus,
} from "./types.js";

// ---- PUBLIC (resident) -----------------------------------------------------

export function uploadAttachment(file: File, onProgress?: (pct: number) => void): Promise<string> {
  return USE_MOCKS ? mockUpload(file, onProgress) : uploadAttachmentLive(file, onProgress);
}

export function submitReport(input: SubmitReportInput): Promise<SubmitReportResult> {
  return USE_MOCKS ? mockSubmit(input) : submitReportLive(input);
}

export function getPublicTracking(code: string): Promise<PublicTracking | null> {
  return USE_MOCKS ? mockDb.getPublicTracking(code) : getPublicTrackingLive(code);
}

/**
 * Resident reply/comment on their own tracking page (no login; report-a-concern.md "Resident can
 * reply / add a comment"). `captchaToken` is accepted for parity with the request body shape but
 * the real gate is the caller resolving `ensureCaptchaToken(container)` (wafCaptcha.ts) first, same
 * as submitReport — the live WAF token rides as the `x-aws-waf-token` header (and cookie), not a
 * param here.
 */
export function addResidentComment(
  code: string,
  body: string,
  captchaToken?: string,
): Promise<{ id: string; body: string; createdAt: string }> {
  return USE_MOCKS
    ? mockDb.addResidentComment(code, body)
    : addResidentCommentLive(code, body, captchaToken);
}

/** Post-resolution satisfaction survey (report-a-concern.md "Satisfaction survey after resolution"). */
export function submitSatisfactionSurvey(
  code: string,
  rating: number,
  comment?: string,
): Promise<void> {
  return USE_MOCKS
    ? mockDb.submitSatisfactionSurvey(code, rating, comment)
    : submitSatisfactionSurveyLive(code, rating, comment);
}

/**
 * Reverse-geocode GPS coords → a street address label ("Use my location", ADR-0008 follow-up).
 * Public, no auth. Returns null when there's no match — callers fall back to the typed address.
 */
export function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  return USE_MOCKS ? mockReverseGeocode(lat, lng) : reverseGeocodeLive(lat, lng);
}

// ---- STAFF: queue + detail -------------------------------------------------

/** Staff-initiated intake — POST /api/requests (or the mock store). */
export function createRequest(input: CreateRequestInput): Promise<CreateRequestResult> {
  return USE_MOCKS ? mockDb.createRequest(input) : createRequestLive(input);
}

export type { CreateRequestInput, CreateRequestResult, IntakeChannel } from "./staffApi.js";

export function listQueue(q?: string): Promise<QueueRow[]> {
  return USE_MOCKS ? mockDb.listQueue(q) : listQueueLive(q);
}

// ---- Saved views (queue power-tools) ---------------------------------------

export function listSavedViews(): Promise<SavedView[]> {
  return USE_MOCKS ? mockDb.listSavedViews() : listSavedViewsLive();
}

export function saveSavedView(name: string, filters: SavedViewFilters): Promise<SavedView> {
  return USE_MOCKS ? mockDb.saveSavedView(name, filters) : saveSavedViewLive(name, filters);
}

export function deleteSavedView(id: string): Promise<void> {
  return USE_MOCKS ? mockDb.deleteSavedView(id) : deleteSavedViewLive(id);
}

// ---- Coaching state (coaching framework, issue #501) -----------------------
// Singleton per-user record (#504) — NOT a per-key item; see api/types.ts's CoachingState doc.

export function getCoachingState(): Promise<CoachingState> {
  return USE_MOCKS ? mockDb.getCoachingState() : getCoachingStateLive();
}

export function putCoachingState(patch: CoachingStatePatch): Promise<CoachingState> {
  return USE_MOCKS ? mockDb.putCoachingState(patch) : putCoachingStateLive(patch);
}

/** Resets the caller's ENTIRE coaching record — there is no per-key delete route. */
export function deleteCoachingState(): Promise<void> {
  return USE_MOCKS ? mockDb.deleteCoachingState() : deleteCoachingStateLive();
}

// ---- Bulk queue actions (queue power-tools) --------------------------------

export function bulkAssign(
  requestIds: string[],
  assigneeId: string | null,
  actorId: string,
): Promise<BulkItemResult[]> {
  return USE_MOCKS
    ? mockDb.bulkAssign(requestIds, assigneeId, actorId)
    : bulkAssignLive(requestIds, assigneeId);
}

export function bulkChangeStatus(
  requestIds: string[],
  status: RequestStatus,
  actorId: string,
  publicUpdate?: string,
): Promise<BulkItemResult[]> {
  return USE_MOCKS
    ? mockDb.bulkChangeStatus(requestIds, status, actorId, publicUpdate)
    : bulkChangeStatusLive(requestIds, status, publicUpdate);
}

export function getRequest(id: string): Promise<StaffRequestDetail | null> {
  return USE_MOCKS ? mockDb.getRequest(id) : getRequestLive(id);
}

/** assignee display name — mock keeps a directory; live resolves from the staff list (see console). */
export function assigneeName(id: string | null): string | null {
  return mockDb.assigneeName(id);
}

export async function changeStatus(
  id: string,
  toStatus: RequestStatus,
  opts: { publicUpdate?: string; reason?: string; actorId: string },
): Promise<StaffRequestDetail | null> {
  if (USE_MOCKS) return mockDb.changeStatus(id, toStatus, opts);
  await changeStatusLive(id, toStatus, opts);
  return getRequestLive(id);
}

/**
 * `dueDate` (work-item-due-dates.md) is OPTIONAL — omit to leave an existing due date untouched,
 * pass "" to clear it, or "YYYY-MM-DD" to set it. Only ever sent for StaffOnly-category requests.
 */
export async function assignRequest(
  id: string,
  assigneeId: string | null,
  actorId: string,
  dueDate?: string,
): Promise<StaffRequestDetail | null> {
  if (USE_MOCKS) return mockDb.assignRequest(id, assigneeId, actorId, dueDate);
  await assignRequestLive(id, assigneeId, dueDate);
  return getRequestLive(id);
}

export async function addNote(
  id: string,
  body: string,
  visibility: Visibility,
  actorId: string,
): Promise<StaffRequestDetail | null> {
  if (USE_MOCKS) return mockDb.addNote(id, body, visibility, actorId);
  await addNoteLive(id, body, visibility);
  return getRequestLive(id);
}

/**
 * Staff attach. The mock recorded an already-shaped AttachmentView; the live path uploads the real
 * File then registers it. Callers pass a File on the live path (see ReportDetailPage).
 */
export async function addStaffAttachment(
  id: string,
  file: File,
  kind: AttachmentKind,
  visibility: Visibility,
  onProgress?: (pct: number) => void,
): Promise<StaffRequestDetail | null> {
  if (USE_MOCKS) {
    const isImage = file.type.startsWith("image/");
    return mockDb.addStaffAttachment(
      id,
      {
        url: isImage ? URL.createObjectURL(file) : "",
        filename: file.name,
        contentType: file.type,
        kind,
        visibility,
        uploaderName: mockDb.CURRENT_USER.name,
        isImage,
      },
      mockDb.CURRENT_USER.id,
    );
  }
  await addStaffAttachmentLive(id, file, kind, visibility, onProgress);
  return getRequestLive(id);
}

// ---- ADMIN -----------------------------------------------------------------

export function listCategories(kind?: CategoryKind): Promise<CategoryView[]> {
  return USE_MOCKS ? mockDb.listCategories(kind) : listCategoriesLive(kind);
}
export function saveCategories(next: CategoryView[]): Promise<CategoryView[]> {
  return USE_MOCKS ? mockDb.saveCategories(next) : saveCategoriesLive(next);
}
/** Kind=question categories only — the STAFF-side live topic taxonomy (question console topic
 *  picker, publish-to-FAQ composer, FaqAdmin's topic select). Staff-auth, unfiltered by active
 *  (unlike the public embed-config's questionTopics, an admin still needs to see a deactivated
 *  topic that an old question/FAQ still references). */
export function listQuestionTopics(): Promise<CategoryView[]> {
  return USE_MOCKS ? mockDb.listQuestionTopics() : listQuestionTopicsLive();
}
/** Kind=channel categories only — the STAFF-side live channel taxonomy (CategoriesAdmin's Channels
 *  tab, the broadcast compose "Send to" selector). Staff-auth, unfiltered by active (mirrors
 *  listQuestionTopics above — an admin/compose screen still needs to resolve a deactivated channel
 *  an old draft's audience still references). */
export function listChannels(): Promise<CategoryView[]> {
  return USE_MOCKS ? mockDb.listCategories("channel") : listCategoriesLive("channel");
}
export type { InviteStaffResult, InviteOutcome, ResendOutcome } from "./staffApi.js";
export function listStaff(): Promise<StaffUserView[]> {
  return USE_MOCKS ? mockDb.listStaff() : listStaffLive();
}
export function inviteStaff(
  email: string,
  role: "admin" | "staff" | "contributor",
  name = "",
  department?: string,
): Promise<InviteStaffResult> {
  return USE_MOCKS
    ? mockDb.inviteStaff(email, role, name, department)
    : inviteStaffLive(email, role, name, department);
}
/** Resend a still-pending invite email — PROVISION-IF-MISSING: resolves with "invited" if the row
 *  was a half-state pre-#181 row that just got provisioned instead of a plain resend, or "resent"
 *  for a normal pending-user resend. */
export function resendStaffInvite(user: StaffUserView): Promise<ResendOutcome> {
  return USE_MOCKS ? mockDb.resendStaffInvite(user) : resendStaffInviteLive(user);
}
export function setStaffActive(user: StaffUserView, active: boolean): Promise<StaffUserView> {
  return USE_MOCKS ? mockDb.setStaffActive(user.id, active) : setStaffActiveLive(user, active);
}
export function updateStaff(
  user: StaffUserView,
  changes: { name?: string; role?: "admin" | "staff" | "contributor"; department?: string },
): Promise<StaffUserView> {
  return USE_MOCKS ? mockDb.updateStaff(user.id, changes) : updateStaffLive(user, changes);
}

// ---- Users & Permissions (ADR-0016 §5, RBAC Phase 3) ------------------------
export function getPermissionsRegistry(): Promise<PermissionsRegistryModule[]> {
  return USE_MOCKS ? mockDb.getPermissionsRegistry() : getPermissionsRegistryLive();
}
export function listGroups(): Promise<GroupView[]> {
  return USE_MOCKS ? mockDb.listGroups() : listGroupsLive();
}
export function createGroup(
  name: string,
  description: string,
  permissions: string[],
): Promise<GroupView> {
  return USE_MOCKS
    ? mockDb.createGroup(name, description, permissions)
    : createGroupLive(name, description, permissions);
}
export function updateGroup(
  id: string,
  name: string,
  description: string,
  permissions: string[],
): Promise<GroupView> {
  return USE_MOCKS
    ? mockDb.updateGroup(id, name, description, permissions)
    : updateGroupLive(id, name, description, permissions);
}
export function deleteGroup(id: string): Promise<void> {
  return USE_MOCKS ? mockDb.deleteGroup(id) : deleteGroupLive(id);
}
/** Replace a staff user's ENTIRE group membership list. `actingUserId` is mock-only (the guardrail
 *  check needs to know "who is the caller" — the live API derives it from the JWT). */
export function setStaffGroups(
  staffId: string,
  groupIds: string[],
  actingUserId?: string,
): Promise<GroupView[]> {
  return USE_MOCKS
    ? mockDb.setStaffGroups(staffId, groupIds, actingUserId)
    : setStaffGroupsLive(staffId, groupIds);
}
/** The signed-in user's own effective permissions — usePermissions() calls this once on mount. */
export function getMyPermissions(currentUserId: string): Promise<string[]> {
  return USE_MOCKS ? mockDb.getMyPermissions(currentUserId) : getMyPermissionsLive();
}

export function getBranding(): Promise<BrandingSettingsView> {
  return USE_MOCKS ? mockDb.getBranding() : getBrandingLive();
}
export function saveBranding(next: BrandingSettingsView): Promise<BrandingSettingsView> {
  return USE_MOCKS ? mockDb.saveBranding(next) : saveBrandingLive(next);
}
export type { BrandingSettingsView } from "./staffApi.js";
export function crawlBranding(url: string): Promise<BrandingCrawlResult> {
  return USE_MOCKS ? mockDb.crawlBranding(url) : crawlBrandingLive(url);
}
export type { BrandingCrawlResult } from "./staffApi.js";
/**
 * Upload a (already client-downscaled) logo blob and return the short path to store as
 * `form.logo` — S3-hosted via presign in live mode; an object URL "path" in mock mode so
 * dev-with-mocks exercises the same request → PUT → set-path flow offline.
 */
export function uploadLogo(blob: Blob, extension: string): Promise<string> {
  return USE_MOCKS ? mockDb.uploadLogo(blob, extension) : uploadLogoLive(blob, extension);
}

/**
 * Upload a document (PDF or image) for linking from a FAQ answer — S3-hosted via presign in live
 * mode; a deterministic `/docs/...` path in mock mode. Used by RichTextEditor's "Insert document" AND
 * by the meeting-level "Upload a file" attach mode (MeetingLevelAttachmentsSection). `onProgress`
 * (percent 0-100) drives an upload-progress readout — optional, existing callers unaffected.
 */
export function uploadDocument(file: File, onProgress?: (pct: number) => void): Promise<string> {
  return USE_MOCKS ? mockDb.uploadDocument(file, onProgress) : uploadDocumentLive(file, onProgress);
}
export function listTemplates(): Promise<NotificationTemplateView[]> {
  return USE_MOCKS ? mockDb.listTemplates() : listTemplatesLive();
}
export function saveTemplate(t: NotificationTemplateView): Promise<NotificationTemplateView> {
  return USE_MOCKS ? mockDb.saveTemplate(t) : saveTemplateLive(t);
}

export { CURRENT_USER } from "./mockDb.js";

// ---- Embed config (brand + categories + question topics + channels) --------
import type { EmbedConfig } from "../theme/embedConfig.js";
import type { IntakeCategory } from "./categories.js";
import { MOCK_CATEGORIES } from "./categories.js";
import type { QuestionTopic } from "./questionTopics.js";
import { MOCK_QUESTION_TOPICS } from "./questionTopics.js";
import { MOCK_CHANNELS } from "./channels.js";
import type { KORARecordType } from "./koraRecordTypes.js";
import { MOCK_KORA_RECORD_TYPES } from "./koraRecordTypes.js";
import type { PermitType } from "./permitTypes.js";
import { MOCK_PERMIT_TYPES } from "./permitTypes.js";
import { DEFAULT_KORA_CONFIG, type KORAEmbedConfig } from "./embedConfigMap.js";
import { fetchEmbedConfig as fetchMockEmbedConfig } from "../theme/mockEmbedConfig.js";
import { fetchEmbedConfigLive } from "./publicApi.js";

/**
 * Load the town brand + active concern-category list + active question-topic list + active
 * channel list + active KORA record-type list + the KORA respond-days/citation config — live from
 * /api/public/embed-config (server-split by Kind, handler/public.go), or the mock. Every resident
 * surface that needs topics/channels/record-types (AskPage/FaqPage, SubscribePage/PreferencesPage,
 * RecordsRequestPage) should read from here rather than importing the MOCK_* lists directly, so
 * live mode always reflects the town's own configuration.
 */
export async function fetchEmbedConfig(): Promise<{
  config: EmbedConfig;
  categories: IntakeCategory[];
  questionTopics: QuestionTopic[];
  channels: ChannelView[];
  koraRecordTypes: KORARecordType[];
  permitTypes: PermitType[];
  kora: KORAEmbedConfig;
  publicShowAgendaItemDetails: boolean;
  publicShowAttachments: boolean;
}> {
  if (USE_MOCKS) {
    const config = await fetchMockEmbedConfig();
    // Mock mode reads the SAME in-memory TenantSettings the admin toggles edit (mockDb.getBranding)
    // so BrandingAdmin's Meetings toggles actually affect what MeetingsPage renders in dev/test.
    const branding = await mockDb.getBranding();
    return {
      config,
      categories: MOCK_CATEGORIES,
      questionTopics: MOCK_QUESTION_TOPICS,
      channels: MOCK_CHANNELS,
      koraRecordTypes: MOCK_KORA_RECORD_TYPES,
      permitTypes: MOCK_PERMIT_TYPES,
      kora: DEFAULT_KORA_CONFIG,
      publicShowAgendaItemDetails: branding.publicShowAgendaItemDetails !== false,
      publicShowAttachments: branding.publicShowAttachments !== false,
    };
  }
  const {
    config,
    categories,
    questionTopics,
    channels,
    koraRecordTypes,
    permitTypes,
    kora,
    publicShowAgendaItemDetails,
    publicShowAttachments,
  } = await fetchEmbedConfigLive();
  // If the town has no categories/topics/channels/record-types/permit-types configured yet, fall
  // back to the built-in defaults.
  return {
    config,
    categories: categories.length ? categories : MOCK_CATEGORIES,
    questionTopics: questionTopics.length ? questionTopics : MOCK_QUESTION_TOPICS,
    channels: channels.length ? channels : MOCK_CHANNELS,
    koraRecordTypes: koraRecordTypes.length ? koraRecordTypes : MOCK_KORA_RECORD_TYPES,
    permitTypes: permitTypes.length ? permitTypes : MOCK_PERMIT_TYPES,
    kora,
    publicShowAgendaItemDetails,
    publicShowAttachments,
  };
}

// ---- Notify: public subscribe / confirm / unsubscribe (ADR-0012) -----------

/** `email` is optional (mail-only signups, postal-updates.md); `deliveryMethod`/`mailingAddress`
 *  are the delivery-preference extension — omitted deliveryMethod defaults to "email". */
export function subscribe(
  email: string | undefined,
  name?: string,
  channels?: string[],
  deliveryMethod?: DeliveryMethod,
  mailingAddress?: MailingAddress,
): Promise<SubscribeResult> {
  return USE_MOCKS
    ? mockDb.subscribe(email, name, channels, deliveryMethod, mailingAddress)
    : subscribeLive({ email, name, channels, deliveryMethod, mailingAddress });
}

export function resendConfirmation(email: string): Promise<void> {
  return USE_MOCKS ? mockDb.resendConfirmation(email) : resendConfirmationLive(email);
}

/** POST /public/notify/manage-link — "email me my manage link" (subscriber-self-service.md Part A).
 *  ALWAYS resolves {status:"ok"} — anti-enumeration; the UI shows the same generic success copy no
 *  matter what. */
export function manageLink(email: string): Promise<{ status: "ok" }> {
  return USE_MOCKS ? mockDb.manageLink(email) : manageLinkLive(email);
}

export function confirmSubscription(token: string): Promise<NotifyLandingResult> {
  return USE_MOCKS ? mockDb.confirmSubscription(token) : confirmSubscriptionLive(token);
}

export function unsubscribe(token: string): Promise<NotifyLandingResult> {
  return USE_MOCKS ? mockDb.unsubscribe(token) : unsubscribeLive(token);
}

/** GET preferences (stay-informed-channels.md §3.2, Phase 4) — the subscriber's current channel
 *  selection + the tenant's active channel list. */
export function getPreferences(token: string): Promise<PreferencesResult> {
  return USE_MOCKS ? mockDb.getPreferences(token) : getPreferencesLive(token);
}

/** PUT preferences — replace the subscriber's channel selection; an empty `channels` array routes
 *  to a real unsubscribe (§3.2 "uncheck-all → unsubscribe"). `deliveryMethod`/`mailingAddress` are
 *  optional — omitted means no change (postal-updates.md). */
export function updatePreferences(
  token: string,
  channels: string[],
  deliveryMethod?: DeliveryMethod,
  mailingAddress?: MailingAddress,
): Promise<PreferencesUpdateResult> {
  return USE_MOCKS
    ? mockDb.updatePreferences(token, channels, deliveryMethod, mailingAddress)
    : updatePreferencesLive(token, channels, deliveryMethod, mailingAddress);
}

// ---- Notify: staff Broadcast console (ADR-0012) -----------------------------

export function listBroadcasts(): Promise<BroadcastRow[]> {
  return USE_MOCKS ? mockDb.listBroadcasts() : listBroadcastsLive();
}

export function getBroadcast(id: string): Promise<BroadcastDetail | null> {
  return USE_MOCKS ? mockDb.getBroadcast(id) : getBroadcastLive(id);
}

export function saveBroadcastDraft(
  input: CreateBroadcastInput,
  id?: string,
): Promise<BroadcastDetail> {
  return USE_MOCKS ? mockDb.saveBroadcastDraft(input, id) : saveBroadcastDraftLive(input, id);
}

export function sendBroadcast(id: string): Promise<SendBroadcastResult> {
  return USE_MOCKS ? mockDb.sendBroadcast(id) : sendBroadcastLive(id);
}

export function sendBroadcastTest(id: string): Promise<void> {
  return USE_MOCKS ? mockDb.sendBroadcastTest(id) : sendBroadcastTestLive(id);
}

// ---- Analytics (mayor dashboard) -------------------------------------------

/** GET /api/analytics — staff-auth, tenant-scoped from the Cognito JWT. Aggregate-only: never
 *  returns reporter PII, contact info, or individual complaint descriptions. `start`/`end` are
 *  ISO date strings; omit both for the server/mock default (trailing 90 days). */
export function getAnalytics(start?: string, end?: string): Promise<AnalyticsView> {
  return USE_MOCKS ? mockDb.getAnalytics(start, end) : getAnalyticsLive(start, end);
}
export type { AnalyticsView } from "./types.js";

/** FAQ-only analytics block (used where a surface wants just the FAQ block without a full
 *  getAnalytics() range fetch — e.g. a future standalone FAQ dashboard card). */
export async function getFAQAnalytics(): Promise<FAQAnalytics | undefined> {
  return USE_MOCKS ? mockGetFAQAnalytics() : getFAQAnalyticsLive();
}

// ---- Ask a Question + FAQ: resident (public) --------------------------------

export function submitQuestion(input: SubmitQuestionInput): Promise<SubmitReportResult> {
  return USE_MOCKS ? mockSubmitQuestion(input) : submitQuestionLive(input);
}
export type { SubmitQuestionInput } from "./publicApi.js";

/** GET /public/faq — browse published FAQs (featured-first, by topic). */
export function browseFAQ(topic?: string): Promise<FAQView[]> {
  return USE_MOCKS ? mockBrowseFAQ(topic) : browseFAQLive(topic);
}

/** GET /public/faq/search — keyword search; also backs the related-FAQ typeahead (deflect before
 *  asking). */
export function searchFAQ(q: string, topic?: string): Promise<FAQView[]> {
  return USE_MOCKS ? mockSearchFAQ(q, topic) : searchFAQLive(q, topic);
}

export function getFAQ(id: string): Promise<FAQView | null> {
  return USE_MOCKS ? mockGetFAQ(id) : getFAQLive(id);
}

/** POST /public/faq/{id}/vote — "was this helpful?" */
export function voteFAQ(id: string, helpful: boolean): Promise<void> {
  return USE_MOCKS ? mockVoteFAQ(id, helpful) : voteFAQLive(id, helpful);
}
export type { FAQView } from "./types.js";

// ---- Ask a Question + FAQ: staff console + admin FAQ ------------------------

export function listQuestionQueue(q?: string): Promise<QuestionQueueRow[]> {
  return USE_MOCKS ? mockListQuestionQueue(q) : listQuestionQueueLive(q);
}
export type { QuestionQueueRow, StaffQuestionDetail } from "./types.js";

export function getQuestion(id: string): Promise<StaffQuestionDetail | null> {
  return USE_MOCKS ? mockGetQuestion(id) : getQuestionLive(id);
}

export async function answerQuestion(
  id: string,
  answer: string,
  actorName: string,
): Promise<StaffQuestionDetail | null> {
  if (USE_MOCKS) return mockAnswerQuestion(id, answer, actorName);
  await answerQuestionLive(id, answer);
  return getQuestionLive(id);
}

export async function closeQuestion(id: string): Promise<StaffQuestionDetail | null> {
  if (USE_MOCKS) return mockCloseQuestion(id);
  await closeQuestionLive(id);
  return getQuestionLive(id);
}

/** Assign (♻️ mirrors assignKORA/assignCase/assignPermit) — a question IS a Request on the live
 *  path (assignRequestLive posts /requests/{id}/assign, kind-agnostic), but the mock store keeps
 *  QUESTIONS separate from REQUESTS, so it gets its own mock facade. */
export async function assignQuestion(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffQuestionDetail | null> {
  if (USE_MOCKS) return mockAssignQuestion(id, assigneeId, actorId);
  await assignRequestLive(id, assigneeId);
  return getQuestionLive(id);
}

export function listAdminFAQ(): Promise<FAQView[]> {
  return USE_MOCKS ? mockListAdminFAQ() : listAdminFAQLive();
}

export function publishFAQ(
  input: PublishFAQInput,
): Promise<{ faq: FAQView; mergedQuestionIds: string[] }> {
  return USE_MOCKS ? mockPublishFAQ(input) : publishFAQLive(input);
}
export type { PublishFAQInput } from "./types.js";

export function updateFAQ(id: string, input: UpdateFAQInput): Promise<FAQView | null> {
  return USE_MOCKS ? mockUpdateFAQ(id, input) : updateFAQLive(id, input);
}
export type { UpdateFAQInput } from "./types.js";

export function suggestFAQFromConcern(requestId: string): Promise<SuggestFAQDraft | null> {
  return USE_MOCKS ? mockSuggestFAQFromConcern(requestId) : suggestFAQFromConcernLive(requestId);
}
export type { SuggestFAQDraft } from "./types.js";

// ---- Open Records (KORA) requests: resident (public) + staff console -------------------------
//
// POST /public/records-requests (♻️ submitReport/submitQuestion shape); GET /admin/kora,
// GET /admin/kora/{id}, POST /admin/kora/{id}/respond (staff console, records-request.md). assign/
// addNote/addStaffAttachment are Kind-agnostic on the LIVE path (a KORA request IS a Request, same
// storage) but the MOCK store keeps KORA in its own array (disjoint status value-space + response
// fields neither concern nor question carries) — so KORA gets its OWN assign/note/attach mock
// facade functions rather than reusing assignRequest/addNote/addStaffAttachment.

export function submitKORA(input: SubmitKORAInput): Promise<SubmitReportResult> {
  return USE_MOCKS ? mockSubmitKORA(input) : submitKORALive(input);
}
export type { SubmitKORAInput } from "./types.js";

export function listKORAQueue(q?: string): Promise<KORAQueueRow[]> {
  return USE_MOCKS ? mockListKORAQueue(q) : listKORAQueueLive(q);
}
export type { KORAQueueRow, StaffKORADetail } from "./types.js";

export function getKORA(id: string): Promise<StaffKORADetail | null> {
  return USE_MOCKS ? mockGetKORA(id) : getKORALive(id);
}

export async function respondKORA(
  id: string,
  input: RespondKORAInput,
): Promise<StaffKORADetail | null> {
  if (USE_MOCKS) return mockRespondKORA(id, input);
  await respondKORALive(id, input);
  return getKORALive(id);
}
export type { RespondKORAInput } from "./types.js";

export async function assignKORA(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffKORADetail | null> {
  if (USE_MOCKS) return mockAssignKORA(id, assigneeId, actorId);
  await assignRequestLive(id, assigneeId);
  return getKORALive(id);
}

export async function addKORANote(
  id: string,
  body: string,
  visibility: Visibility,
  actorId: string,
): Promise<StaffKORADetail | null> {
  if (USE_MOCKS) return mockAddKORANote(id, body, visibility, actorId);
  await addNoteLive(id, body, visibility);
  return getKORALive(id);
}

/** Attach a produced record (e.g. the fulfilled documents) to a KORA request via the doc store —
 *  same presign→PUT→register flow as staff concern attachments. */
export async function addKORAAttachment(
  id: string,
  file: File,
  kind: AttachmentKind,
  visibility: Visibility,
  onProgress?: (pct: number) => void,
): Promise<StaffKORADetail | null> {
  if (USE_MOCKS) {
    const isImage = file.type.startsWith("image/");
    return mockAddKORAAttachment(
      id,
      {
        url: isImage ? URL.createObjectURL(file) : "",
        filename: file.name,
        contentType: file.type,
        kind,
        visibility,
        uploaderName: mockDb.CURRENT_USER.name,
        isImage,
      },
      mockDb.CURRENT_USER.id,
    );
  }
  await addStaffAttachmentLive(id, file, kind, visibility, onProgress);
  return getKORALive(id);
}

/** Kind=kora categories only — the STAFF-side live record-type taxonomy (KORADetailPage's record
 *  type, CategoriesAdmin's KORA tab). Staff-auth, unfiltered by active (♻️ mirrors listQuestionTopics). */
export function listKORARecordTypes(): Promise<CategoryView[]> {
  return USE_MOCKS ? mockListKORARecordTypes() : listKORARecordTypesLive();
}

// ---- Code Enforcement (Case) requests: staff-only console (handler/case.go) ------------------
//
// POST /admin/cases (open), GET /admin/cases (queue), POST /admin/cases/{id}/advance (lifecycle).
// STAFF-ONLY: there is no public/resident intake — an officer opens a case (handler.OpenCase doc).
// assign/addNote/addStaffAttachment are Kind-agnostic on the LIVE path (a case IS a Request, same
// storage) but the MOCK store keeps cases in their own array (disjoint status value-space +
// lifecycle fields no sibling shape has) — so cases get their OWN assign/note/attach mock facade
// functions rather than reusing assignRequest/addNote/addStaffAttachment (♻️ mirrors KORA's pattern).

export function openCase(input: OpenCaseInput): Promise<OpenCaseResult> {
  return USE_MOCKS ? mockOpenCase(input) : openCaseLive(input);
}
export type { OpenCaseInput, OpenCaseResult } from "./types.js";

export function listCases(q?: string): Promise<CaseQueueRow[]> {
  return USE_MOCKS ? mockListCases(q) : listCasesLive(q);
}
export type { CaseQueueRow, StaffCaseDetail } from "./types.js";

export function getCase(id: string): Promise<StaffCaseDetail | null> {
  return USE_MOCKS ? mockGetCase(id) : getCaseLive(id);
}

export async function advanceCase(
  id: string,
  input: AdvanceCaseInput,
): Promise<StaffCaseDetail | null> {
  if (USE_MOCKS) return mockAdvanceCase(id, input);
  await advanceCaseLive(id, input);
  return getCaseLive(id);
}
export type { AdvanceCaseInput } from "./types.js";

export async function assignCase(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffCaseDetail | null> {
  if (USE_MOCKS) return mockAssignCase(id, assigneeId, actorId);
  await assignRequestLive(id, assigneeId);
  return getCaseLive(id);
}

export async function addCaseNote(
  id: string,
  body: string,
  visibility: Visibility,
  actorId: string,
): Promise<StaffCaseDetail | null> {
  if (USE_MOCKS) return mockAddCaseNote(id, body, visibility, actorId);
  await addNoteLive(id, body, visibility);
  return getCaseLive(id);
}

/** Attach a site photo / certified-mail receipt / letter to a case via the doc store — same
 *  presign→PUT→register flow as staff concern attachments. */
export async function addCaseAttachment(
  id: string,
  file: File,
  kind: AttachmentKind,
  visibility: Visibility,
  onProgress?: (pct: number) => void,
): Promise<StaffCaseDetail | null> {
  if (USE_MOCKS) {
    const isImage = file.type.startsWith("image/");
    return mockAddCaseAttachment(
      id,
      {
        url: isImage ? URL.createObjectURL(file) : "",
        filename: file.name,
        contentType: file.type,
        kind,
        visibility,
        uploaderName: mockDb.CURRENT_USER.name,
        isImage,
      },
      mockDb.CURRENT_USER.id,
    );
  }
  await addStaffAttachmentLive(id, file, kind, visibility, onProgress);
  return getCaseLive(id);
}

/** Kind=case categories only — the STAFF-side live violation-type taxonomy (CaseDetailPage's
 *  violation type, CategoriesAdmin's "Violation types" tab). Staff-auth, unfiltered by active
 *  (♻️ mirrors listKORARecordTypes). */
export function listCaseViolationTypes(): Promise<CategoryView[]> {
  return USE_MOCKS ? mockListCaseViolationTypes() : listCaseViolationTypesLive();
}

// ---- Permits & Licensing (roadmap module 6): resident (public) + staff console ----------------
//
// POST /public/permits (♻️ submitReport/submitKORA shape); GET /admin/permits, GET /requests/{id},
// POST /admin/permits/{id}/advance, POST /admin/permits/{id}/inspection (staff console). Unlike
// Case, a permit DOES have a public intake — an applicant files their own application (like KORA).
// assign/addNote/addStaffAttachment are Kind-agnostic on the LIVE path (a permit IS a Request, same
// storage) but the MOCK store keeps permits in their own array (disjoint status value-space +
// lifecycle fields no sibling shape has) — so permits get their OWN assign/note/attach mock facade
// functions rather than reusing assignRequest/addNote/addStaffAttachment (♻️ mirrors KORA/Case's
// pattern).

export function submitPermit(input: SubmitPermitInput): Promise<SubmitReportResult> {
  return USE_MOCKS ? mockSubmitPermit(input) : submitPermitLive(input);
}
export type { SubmitPermitInput } from "./types.js";

export function listPermits(q?: string): Promise<PermitQueueRow[]> {
  return USE_MOCKS ? mockListPermits(q) : listPermitsLive(q);
}
export type { PermitQueueRow, StaffPermitDetail } from "./types.js";

export function getPermit(id: string): Promise<StaffPermitDetail | null> {
  return USE_MOCKS ? mockGetPermit(id) : getPermitLive(id);
}

export async function advancePermit(
  id: string,
  input: AdvancePermitInput,
): Promise<StaffPermitDetail | null> {
  if (USE_MOCKS) return mockAdvancePermit(id, input);
  await advancePermitLive(id, input);
  return getPermitLive(id);
}
export type { AdvancePermitInput } from "./types.js";

export async function recordPermitInspection(
  id: string,
  input: RecordInspectionInput,
): Promise<StaffPermitDetail | null> {
  if (USE_MOCKS) return mockRecordPermitInspection(id, input);
  await recordInspectionLive(id, input);
  return getPermitLive(id);
}
export type { RecordInspectionInput } from "./types.js";

export async function assignPermit(
  id: string,
  assigneeId: string | null,
  actorId: string,
): Promise<StaffPermitDetail | null> {
  if (USE_MOCKS) return mockAssignPermit(id, assigneeId, actorId);
  await assignRequestLive(id, assigneeId);
  return getPermitLive(id);
}

export async function addPermitNote(
  id: string,
  body: string,
  visibility: Visibility,
  actorId: string,
): Promise<StaffPermitDetail | null> {
  if (USE_MOCKS) return mockAddPermitNote(id, body, visibility, actorId);
  await addNoteLive(id, body, visibility);
  return getPermitLive(id);
}

/** Attach a plan/document to a permit application via the doc store — same presign→PUT→register
 *  flow as staff concern attachments. */
export async function addPermitAttachment(
  id: string,
  file: File,
  kind: AttachmentKind,
  visibility: Visibility,
  onProgress?: (pct: number) => void,
): Promise<StaffPermitDetail | null> {
  if (USE_MOCKS) {
    const isImage = file.type.startsWith("image/");
    return mockAddPermitAttachment(
      id,
      {
        url: isImage ? URL.createObjectURL(file) : "",
        filename: file.name,
        contentType: file.type,
        kind,
        visibility,
        uploaderName: mockDb.CURRENT_USER.name,
        isImage,
      },
      mockDb.CURRENT_USER.id,
    );
  }
  await addStaffAttachmentLive(id, file, kind, visibility, onProgress);
  return getPermitLive(id);
}

/** Kind=permit categories only — the STAFF-side live permit-type taxonomy (PermitDetailPage's
 *  permit type, CategoriesAdmin's "Permit types" tab). Staff-auth, unfiltered by active
 *  (♻️ mirrors listCaseViolationTypes). */
export function listPermitTypes(): Promise<CategoryView[]> {
  return USE_MOCKS ? mockListPermitTypes() : listPermitTypesLive();
}

// ---- Contractor Registry (docs/design/contractor-registry/spec.md, PR 2/4): staff console +
// public (no-login) directory -------------------------------------------------------------------
//
// GET/POST /admin/contractors, GET/PUT /admin/contractors/{id}, POST .../renew, POST .../notes
// (staff, RBAC-gated server-side on perms.ModuleContractors); GET /public/contractors[?trade=&q=],
// GET /public/contractors/{id} (no auth — the server enforces visibility=='Public' AND
// status=='Approved' on every response; this facade never widens or re-filters what comes back).

export function listContractors(): Promise<ContractorQueueRow[]> {
  return USE_MOCKS ? mockListContractors() : listContractorsLive();
}
export type { ContractorQueueRow, ContractorDetailView, ContractorView } from "./types.js";

export function getContractor(id: string): Promise<ContractorDetailView | null> {
  return USE_MOCKS ? mockGetContractor(id) : getContractorLive(id);
}

/** POST (create, id omitted) or PUT (update, id set) — mirrors the server's upsert convention. */
export function putContractor(input: PutContractorInput, id?: string): Promise<ContractorView> {
  return USE_MOCKS ? mockPutContractor(input, id) : putContractorLive(input, id);
}
export type { PutContractorInput } from "./types.js";

export function renewContractor(
  id: string,
  input: RenewContractorInput,
): Promise<ContractorView> {
  return USE_MOCKS ? mockRenewContractor(id, input) : renewContractorLive(id, input);
}
export type { RenewContractorInput } from "./types.js";

export async function addContractorNote(
  id: string,
  body: string,
): Promise<ContractorDetailView | null> {
  if (USE_MOCKS) {
    await mockAddContractorNote(id, body);
    return mockGetContractor(id);
  }
  await addContractorNoteLive(id, body);
  return getContractorLive(id);
}

/** Kind=trade categories only — the tenant-configurable trade taxonomy (spec §2.1), staff-auth,
 *  unfiltered by active (♻️ mirrors listCaseViolationTypes/listPermitTypes). STAFF SURFACES ONLY
 *  (the Contractors console) — a resident has no session, so this 401s there. The public directory
 *  must use listPublicTradeCategories below instead. */
export function listTradeCategories(): Promise<CategoryView[]> {
  return USE_MOCKS ? mockListTradeCategories() : listTradeCategoriesLive();
}

/** GET /public/contractor-trades — the PUBLIC trade taxonomy (no auth) that backs the public
 *  directory's per-trade filter pills + id→label resolution (fix/contractor-directory-render-and-
 *  nav). Mock mode reuses the same unauthenticated mock trade list as the staff call — the staff
 *  endpoint's mock never required auth, so there was nothing to reproduce there; this split only
 *  matters against the real API. */
export function listPublicTradeCategories(): Promise<PublicTradeCategory[]> {
  return USE_MOCKS ? mockListTradeCategories() : listPublicTradeCategoriesLive();
}

/** GET /public/contractors?trade=&q= — the public directory list (spec §3c); also backs the
 *  permit-picker's registered-contractor dropdown (PR 3, same predicate). */
export function listPublicContractors(
  trade?: string,
  q?: string,
): Promise<PublicContractorView[]> {
  return USE_MOCKS
    ? mockListPublicContractors(trade, q)
    : listPublicContractorsLive(trade, q);
}
export type { PublicContractorView } from "./types.js";

/** GET /public/contractors/{id} — resolves `null` for every non-matching case (doesn't exist,
 *  Private, Pending, Suspended, Expired) — all indistinguishable by design (spec §3c). */
export function getPublicContractor(id: string): Promise<PublicContractorView | null> {
  return USE_MOCKS ? mockGetPublicContractor(id) : getPublicContractorLive(id);
}

// ---- Permit → POA daily digest — "Log a paper permit" (docs/features/permit-poa-digest.md) ------

/** POST /api/admin/permits/ocr — explicit/opt-in AI "Read with AI" action on a scanned permit file.
 *  Throws ApiError with code "ai_not_configured"/"ai_monthly_cap_reached" (503), "ai_unavailable"
 *  (502), or "unsupported_file_type"/"file_too_large"/"unknown_permit_type" (400). */
export function ocrPermitFields(input: OcrPermitFieldsInput): Promise<OcrPermitFieldsResult> {
  return USE_MOCKS ? mockOcrPermitFields(input) : ocrPermitFieldsLive(input);
}
export type { OcrPermitFieldsInput, OcrPermitFieldsResult } from "./types.js";
export { LOG_PAPER_PERMIT_CONFIDENCE_THRESHOLD } from "./types.js";

/** POST /api/admin/permits — staff-counter/paper/scanned permit intake, clerk-chosen initial
 *  status. Returns a thin ack; callers re-fetch via getPermit(id) to land on the real detail page. */
export function createPermitByStaff(
  input: CreatePermitByStaffInput,
): Promise<CreatePermitByStaffResult> {
  return USE_MOCKS ? mockCreatePermitByStaff(input) : createPermitByStaffLive(input);
}
export type { CreatePermitByStaffInput, CreatePermitByStaffResult } from "./types.js";

/** POST /api/admin/subscribers — staff-added subscriber, confirmed immediately (no opt-in email). */
export function addSubscriberByStaff(input: AddSubscriberByStaffInput): Promise<SubscriberView> {
  return USE_MOCKS ? mockAddSubscriberByStaff(input) : addSubscriberByStaffLive(input);
}
export type { AddSubscriberByStaffInput } from "./types.js";

// ---- Building & Code Enforcement Council Report (docs/features/council-report.md) -------------
//
// GET /api/admin/reports/council?scope=permits|cases|combined&from=&to= — a staff-triggered,
// READ-ONLY, scope-selectable activity report over the live Permits (Kind=permit) and/or Code
// Enforcement (Kind=case) data for a date range. Every figure is COMPUTED server-side; the AI
// narrative is prose-only, never a number.

/** Fetch the HTML-first report + its structured figures for the given scope/range (from/to are
 *  calendar dates "YYYY-MM-DD", both required — no default range). */
export function getCouncilReport(
  scope: CouncilReportScope,
  from: string,
  to: string,
): Promise<CouncilReportResult> {
  return USE_MOCKS
    ? mockDb.getCouncilReport(scope, from, to)
    : getCouncilReportLive(scope, from, to);
}

/** Fetch the same report rendered as a PDF (same figures, gofpdf-rendered server-side). */
export function getCouncilReportPdf(
  scope: CouncilReportScope,
  from: string,
  to: string,
): Promise<Blob> {
  return USE_MOCKS
    ? mockDb.getCouncilReportPdf(scope, from, to)
    : getCouncilReportPdfLive(scope, from, to);
}

// ---- Postal (snail-mail) updates — staff mailing-list export (docs/features/postal-updates.md) --

/** Fetch the mailing-list CSV (name/address/email/channels/deliveryMethod), optionally filtered to
 *  one channel; the caller mints an object URL to trigger the browser download. */
export function exportMailingListCsv(channel?: string): Promise<Blob> {
  return USE_MOCKS ? mockDb.exportMailingListCsv(channel) : exportMailingListCsvLive(channel);
}

// ---- Subscriber self-service — staff console (docs/features/subscriber-self-service.md Part B) --

/** GET /api/admin/subscribers?q=&status= — searchable/filterable subscriber list (all statuses,
 *  admin-gated PII). `q` is debounced client-side by the caller. */
export function listSubscribers(
  q?: string,
  status?: SubscriptionStatus,
): Promise<SubscriberView[]> {
  return USE_MOCKS ? mockDb.listSubscribers(q, status) : listSubscribersLive(q, status);
}

/** POST /api/admin/subscribers/{id}/unsubscribe — staff-initiated unsubscribe (audited
 *  source="staff" server-side). Idempotent. */
export function unsubscribeSubscriber(id: string): Promise<SubscriberView> {
  return USE_MOCKS ? mockDb.unsubscribeSubscriber(id) : unsubscribeSubscriberLive(id);
}

/** POST /api/admin/subscribers/{id}/resend-confirmation — staff-initiated resend of the
 *  double opt-in confirmation email to a "pending" subscriber. Status stays "pending". */
export function resendSubscriberConfirmation(id: string): Promise<SubscriberView> {
  return USE_MOCKS
    ? mockDb.resendSubscriberConfirmation(id)
    : resendSubscriberConfirmationLive(id);
}

export type {
  CaseFigures,
  CouncilReportFigures,
  CouncilReportResult,
  CouncilReportScope,
  NewCaseOpened,
  NewPermitApplication,
  OverdueCase,
  PermitFigures,
} from "./types.js";
export { COUNCIL_REPORT_SCOPE_LABELS } from "./types.js";

// ---- AI "Clean up" helper (FAQ answer editor) + usage readout ---------------

/** POST /api/admin/ai/cleanup — staff-only, human-in-the-loop: this only returns a SUGGESTED
 *  rewrite; RichTextEditor shows it in a preview panel and never auto-applies it. Throws ApiError
 *  with code "ai_not_configured" (503) if the AI key secret isn't set — callers should show the
 *  friendly inline note, not an error toast — or "ai_unavailable" (502) on an upstream failure. */
export function cleanupAnswer(text: string, mode: AiCleanupMode): Promise<string> {
  return USE_MOCKS ? cleanupAnswerMock(text, mode) : cleanupAnswerLive(text, mode);
}
export type { AiCleanupMode } from "./types.js";

// ---- Council Agenda + Packet (agenda-packet.md §10A Phase 3/3b/4) ----------------------------

import type {
  MeetingView,
  MeetingsListPage,
  MeetingBundle,
  CreateMeetingInput,
  UpdateMeetingInput,
  PublishMeetingInput,
  AgendaItemView,
  AgendaItemInput,
  CreateAgendaItemInput,
  ReorderAgendaItemPosition,
  MeetingAttachmentView,
  AttachMeetingFileInput,
  AttachMeetingLinkInput,
  MinutesView,
  UpsertMinutesInput,
  ContributionView,
  AssignContributionInput,
  SaveContributionInput,
  DraftAgendaSection,
  SummarizeAgendaItemResult,
  PublicMeetingView,
  PublicMeetingDetail,
  PublicConcernsFilter,
  PublicConcernsView,
  StaffConcernsFilter,
  StaffConcernsView,
  MyReportSlot,
  ContributorContributionView,
  ContributorMeetingView,
  ContributorAgendaItemView,
  TranscriptJobState,
  TranscriptJobView,
  RecordingUploadPresign,
  TranscribeMeetingInput,
  ExportPacketInput,
  ExportPacketResult,
  YouTubeStatusView,
  YouTubeAuthorizeView,
  YouTubeTranscriptPullResult,
  MeetingTemplateView,
  CreateMeetingTemplateInput,
  UpdateMeetingTemplateInput,
  MaterializeTemplateInput,
} from "./types.js";
import {
  listMeetingsLive,
  createMeetingLive,
  getMeetingBundleLive,
  updateMeetingLive,
  deleteMeetingLive,
  publishMeetingLive,
  unpublishMeetingLive,
  addAgendaItemLive,
  updateAgendaItemLive,
  deleteAgendaItemLive,
  reorderAgendaItemsLive,
  attachMeetingFileLive,
  attachMeetingLinkLive,
  removeMeetingFileLive,
  saveMeetingMinutesLive,
  adoptMeetingMinutesLive,
  assignContributionLive,
  saveContributionByClerkLive,
  draftAgendaLive,
  summarizeAgendaItemLive,
  presignRecordingUploadLive,
  uploadRecordingLive,
  transcribeMeetingLive,
  getTranscribeStatusLive,
  retryTranscribeLive,
  exportMeetingPacketLive,
  getYouTubeAuthorizeUrlLive,
  getYouTubeStatusLive,
  disconnectYouTubeLive,
  pullYouTubeTranscriptLive,
  listMeetingTemplatesLive,
  createMeetingTemplateLive,
  getMeetingTemplateLive,
  updateMeetingTemplateLive,
  deleteMeetingTemplateLive,
  materializeMeetingTemplateLive,
  saveMeetingAsTemplateLive,
} from "./staffApi.js";
import {
  listPublicMeetingsLive,
  getPublicMeetingLive,
  listMyReportsLive,
  getMyContributionLive,
  saveMyContributionLive,
  submitMyContributionLive,
  attachMyContributionFileLive,
} from "./publicApi.js";
import { listPublicConcernsLive } from "./concernsMap.js";

export type {
  MeetingView,
  MeetingsListPage,
  MeetingStatus,
  AgendaState,
  MeetingBundle,
  AgendaItemView,
  MeetingAttachmentView,
  MeetingAttachmentAccessible,
  MeetingLinkType,
  ContributionView,
  ContributionState,
  MinutesView,
  AdoptionState,
  VoteTally,
  CreateMeetingInput,
  UpdateMeetingInput,
  PublishMeetingInput,
  AgendaItemInput,
  CreateAgendaItemInput,
  ReorderAgendaItemPosition,
  AttachMeetingFileInput,
  AttachMeetingLinkInput,
  UpsertMinutesInput,
  AssignContributionInput,
  SaveContributionInput,
  DraftAgendaSection,
  DraftAgendaRequest,
  SummarizeAgendaItemResult,
  PublicMeetingView,
  PublicAttachmentView,
  PublicAgendaItemView,
  PublicMinutesView,
  PublicMeetingDetail,
  PublicConcernPin,
  PublicConcernsFilter,
  PublicConcernsView,
  PublicConcernStatus,
  MyReportSlot,
  ContributorContributionView,
  ContributorMeetingView,
  ContributorAgendaItemView,
  TranscriptJobState,
  TranscriptJobView,
  RecordingUploadPresign,
  TranscribeMeetingInput,
  ExportPacketInput,
  ExportPacketResult,
  MeetingTemplateView,
  MeetingTemplateSectionView,
  MeetingTemplateItemView,
  CreateMeetingTemplateInput,
  UpdateMeetingTemplateInput,
  MaterializeTemplateInput,
  SaveAsTemplateInput,
} from "./types.js";
export {
  CONTRIBUTION_STATE_LABELS,
  CAN_TRANSITION_CONTRIBUTION,
  RECORDING_UPLOAD_ACCEPT,
  DOC_UPLOAD_ACCEPT,
  MEETING_LINK_TYPE_LABELS,
  MEETINGS_LIST_DEFAULT_PAGE_SIZE,
  MEETINGS_LIST_MAX_PAGE_SIZE,
} from "./types.js";

// ---- Staff builder (admin) --------------------------------------------------

/** One page of the admin Meetings list, newest-first. Omit `cursor` for page 1; pass the prior
 *  page's `nextCursor` to load the next one — an empty `nextCursor` means there is no next page. */
export function listMeetings(cursor?: string, pageSize?: number): Promise<MeetingsListPage> {
  return USE_MOCKS ? mockDb.listMeetings(cursor, pageSize) : listMeetingsLive(cursor, pageSize);
}
export function createMeeting(input: CreateMeetingInput): Promise<MeetingView> {
  return USE_MOCKS ? mockDb.createMeeting(input) : createMeetingLive(input);
}
export function getMeetingBundle(id: string): Promise<MeetingBundle | null> {
  return USE_MOCKS ? mockDb.getMeetingBundle(id) : getMeetingBundleLive(id);
}
export function updateMeeting(id: string, input: UpdateMeetingInput): Promise<MeetingView> {
  return USE_MOCKS ? mockDb.updateMeeting(id, input) : updateMeetingLive(id, input);
}
export function deleteMeeting(id: string): Promise<void> {
  return USE_MOCKS ? mockDb.deleteMeeting(id) : deleteMeetingLive(id);
}
export function publishMeeting(id: string, input?: PublishMeetingInput): Promise<MeetingView> {
  return USE_MOCKS ? mockDb.publishMeeting(id, input) : publishMeetingLive(id, input);
}
export function unpublishMeeting(id: string): Promise<MeetingView> {
  return USE_MOCKS ? mockDb.unpublishMeeting(id) : unpublishMeetingLive(id);
}
export function addAgendaItem(
  meetingId: string,
  input: CreateAgendaItemInput,
): Promise<AgendaItemView> {
  return USE_MOCKS ? mockDb.addAgendaItem(meetingId, input) : addAgendaItemLive(meetingId, input);
}
export function updateAgendaItem(
  meetingId: string,
  itemId: string,
  input: AgendaItemInput,
): Promise<AgendaItemView> {
  return USE_MOCKS
    ? mockDb.updateAgendaItem(meetingId, itemId, input)
    : updateAgendaItemLive(meetingId, itemId, input);
}
export function deleteAgendaItem(meetingId: string, itemId: string): Promise<void> {
  return USE_MOCKS
    ? mockDb.deleteAgendaItem(meetingId, itemId)
    : deleteAgendaItemLive(meetingId, itemId);
}
export function reorderAgendaItems(
  meetingId: string,
  items: ReorderAgendaItemPosition[],
): Promise<void> {
  return USE_MOCKS
    ? mockDb.reorderAgendaItems(meetingId, items)
    : reorderAgendaItemsLive(meetingId, items);
}
export function attachMeetingFile(
  meetingId: string,
  itemId: string,
  input: AttachMeetingFileInput,
): Promise<MeetingAttachmentView> {
  return USE_MOCKS
    ? mockDb.attachMeetingFile(meetingId, itemId, input)
    : attachMeetingFileLive(meetingId, itemId, input);
}
export function removeMeetingFile(
  meetingId: string,
  itemId: string,
  attachmentId: string,
): Promise<void> {
  return USE_MOCKS
    ? mockDb.removeMeetingFile(meetingId, itemId, attachmentId)
    : removeMeetingFileLive(meetingId, itemId, attachmentId);
}
/** A MANUAL, MEETING-LEVEL link attach (no agenda item) — backfills a document the historical
 *  importer missed. Link-only; the server derives the display label from `linkType`. */
export function attachMeetingLink(
  meetingId: string,
  input: AttachMeetingLinkInput,
): Promise<MeetingAttachmentView> {
  return USE_MOCKS
    ? mockDb.attachMeetingLink(meetingId, input)
    : attachMeetingLinkLive(meetingId, input);
}
export function saveMeetingMinutes(
  meetingId: string,
  input: UpsertMinutesInput,
): Promise<MinutesView> {
  return USE_MOCKS
    ? mockDb.saveMeetingMinutes(meetingId, input)
    : saveMeetingMinutesLive(meetingId, input);
}
/** Marks the FORMAL minutes adopted after council approval at a later meeting (one-directional). */
export function adoptMeetingMinutes(meetingId: string): Promise<MinutesView> {
  return USE_MOCKS ? mockDb.adoptMeetingMinutes(meetingId) : adoptMeetingMinutesLive(meetingId);
}
export function assignContribution(
  meetingId: string,
  itemId: string,
  input: AssignContributionInput,
): Promise<ContributionView> {
  return USE_MOCKS
    ? mockDb.assignContribution(meetingId, itemId, input)
    : assignContributionLive(meetingId, itemId, input);
}
export function saveContributionByClerk(
  meetingId: string,
  itemId: string,
  department: string,
  input: SaveContributionInput,
): Promise<ContributionView> {
  return USE_MOCKS
    ? mockDb.saveContributionByClerk(meetingId, itemId, department, input)
    : saveContributionByClerkLive(meetingId, itemId, department, input);
}
/** POST .../draft-agenda — AI "Draft from last meeting" (agenda-packet.md §5.1). Throws ApiError
 *  with code "ai_not_configured" / "ai_monthly_cap_reached" (503) / "ai_unavailable" (502).
 *  `priorTranscript` is optional (see draftAgendaLive doc). */
export function draftAgenda(
  meetingId: string,
  carryOverHint?: string,
  priorTranscript?: string,
): Promise<DraftAgendaSection[]> {
  return USE_MOCKS
    ? mockDb.draftAgenda(meetingId, carryOverHint, priorTranscript)
    : draftAgendaLive(meetingId, carryOverHint, priorTranscript);
}
/** POST /api/admin/meetings/{id}/items/{itemId}/summarize — "Summarize this item from its
 *  attachments": the AI reads the selected attachments and drafts a short item narrative (a
 *  PROPOSAL the clerk edits before saving). See summarizeAgendaItemLive. */
export function summarizeAgendaItem(
  meetingId: string,
  itemId: string,
  attachmentIds: string[],
  hint?: string,
): Promise<SummarizeAgendaItemResult> {
  return USE_MOCKS
    ? mockDb.summarizeAgendaItem(meetingId, itemId, attachmentIds, hint)
    : summarizeAgendaItemLive(meetingId, itemId, attachmentIds, hint);
}
/** POST /api/admin/meetings/{id}/packet — "Export packet": merges the client-generated agenda PDF
 *  with every attachment the server can fetch, returning a presigned download + an
 *  included/skipped manifest (see ExportPacketResult doc). */
export function exportMeetingPacket(
  meetingId: string,
  input: ExportPacketInput,
): Promise<ExportPacketResult> {
  return USE_MOCKS
    ? mockDb.exportMeetingPacket(meetingId, input)
    : exportMeetingPacketLive(meetingId, input);
}

// ---- Meeting Templates (ADR-0021, docs/features/meeting-templates.md) -------------------------

export function listMeetingTemplates(): Promise<MeetingTemplateView[]> {
  return USE_MOCKS ? mockDb.listMeetingTemplates() : listMeetingTemplatesLive();
}
export function createMeetingTemplate(
  input: CreateMeetingTemplateInput,
): Promise<MeetingTemplateView> {
  return USE_MOCKS ? mockDb.createMeetingTemplate(input) : createMeetingTemplateLive(input);
}
export function getMeetingTemplate(templateId: string): Promise<MeetingTemplateView | null> {
  return USE_MOCKS ? mockDb.getMeetingTemplate(templateId) : getMeetingTemplateLive(templateId);
}
export function updateMeetingTemplate(
  templateId: string,
  input: UpdateMeetingTemplateInput,
): Promise<MeetingTemplateView> {
  return USE_MOCKS
    ? mockDb.updateMeetingTemplate(templateId, input)
    : updateMeetingTemplateLive(templateId, input);
}
export function deleteMeetingTemplate(templateId: string): Promise<void> {
  return USE_MOCKS ? mockDb.deleteMeetingTemplate(templateId) : deleteMeetingTemplateLive(templateId);
}
/** Creates a fresh DRAFT meeting from the template; the console redirects into the normal builder
 *  at the returned meeting's id. */
export function materializeTemplate(
  templateId: string,
  input: MaterializeTemplateInput,
): Promise<MeetingView> {
  return USE_MOCKS
    ? mockDb.materializeMeetingTemplate(templateId, input)
    : materializeMeetingTemplateLive(templateId, input);
}
/** Captures an existing meeting's structure (draft or published) as a new template. */
export function saveMeetingAsTemplate(
  meetingId: string,
  name: string,
): Promise<MeetingTemplateView> {
  return USE_MOCKS
    ? mockDb.saveMeetingAsTemplate(meetingId, { name })
    : saveMeetingAsTemplateLive(meetingId, { name });
}

// ---- Council Agenda + Packet — 10B recording->minutes/summary pipeline (agenda-packet.md §5.2) --

export function presignRecordingUpload(
  meetingId: string,
  filename: string,
  contentType: string,
): Promise<RecordingUploadPresign> {
  return USE_MOCKS
    ? mockDb.presignRecordingUpload(meetingId, filename, contentType)
    : presignRecordingUploadLive(meetingId, filename, contentType);
}
/** Full recording upload: presign -> PUT to S3 -> return the s3Key to pass to transcribeMeeting. */
export function uploadRecording(
  meetingId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  return USE_MOCKS
    ? mockDb.uploadRecording(meetingId, file, onProgress)
    : uploadRecordingLive(meetingId, file, onProgress);
}
/** Kick off the async transcribe->minutes/summary pipeline, EITHER from an uploaded recording
 *  (`{recordingS3Key}`, the fallback path) OR a pasted transcript (`{transcript}`, the PRIMARY
 *  path — Linn Valley pastes the YouTube auto-transcript). Throws ApiError 409
 *  "transcription_in_progress" / "already_transcribed" — callers branch on `err.code` (never a raw
 *  error toast for the already-transcribed case; see MeetingBuilderPage's RecordingSection). */
export function transcribeMeeting(
  meetingId: string,
  input: TranscribeMeetingInput,
): Promise<{ state: TranscriptJobState }> {
  return USE_MOCKS
    ? mockDb.transcribeMeeting(meetingId, input)
    : transcribeMeetingLive(meetingId, input);
}
/** Poll target for the pipeline state machine. state:"" = no recording provided yet. */
export function getTranscribeStatus(meetingId: string): Promise<TranscriptJobView> {
  return USE_MOCKS ? mockDb.getTranscribeStatus(meetingId) : getTranscribeStatusLive(meetingId);
}
/** Re-enqueue a FAILED job. Throws ApiError 409 "job_not_failed" otherwise. */
export function retryTranscribe(meetingId: string): Promise<{ state: TranscriptJobState }> {
  return USE_MOCKS ? mockDb.retryTranscribe(meetingId) : retryTranscribeLive(meetingId);
}

// ---- YouTube transcript integration (2026-08-21 reversal) ---------------------------------------

/** GET /api/admin/youtube/connect — mints the authorize URL; the caller redirects the browser to
 *  it (`window.location.href`), it does NOT fetch/navigate itself. */
export function getYouTubeAuthorizeUrl(): Promise<YouTubeAuthorizeView> {
  return USE_MOCKS ? mockDb.getYouTubeAuthorizeUrl() : getYouTubeAuthorizeUrlLive();
}
/** GET /api/admin/youtube/status — connected + (if known) channel title. */
export function getYouTubeStatus(): Promise<YouTubeStatusView> {
  return USE_MOCKS ? mockDb.getYouTubeStatus() : getYouTubeStatusLive();
}
/** DELETE /api/admin/youtube/disconnect. */
export function disconnectYouTube(): Promise<YouTubeStatusView> {
  return USE_MOCKS ? mockDb.disconnectYouTube() : disconnectYouTubeLive();
}
/** POST /api/admin/meetings/{id}/youtube-transcript — pull + stage this meeting's YouTube captions
 *  into the same drafting slot a pasted transcript uses. Throws ApiError with `.code` one of
 *  YouTubeTranscriptErrorCode ("youtube_not_connected" / "no_youtube_link" / "no_caption_tracks" /
 *  "asr_download_failed" / "youtube_unavailable" / "youtube_not_configured"). */
export function pullYouTubeTranscript(meetingId: string): Promise<YouTubeTranscriptPullResult> {
  return USE_MOCKS ? mockDb.pullYouTubeTranscript(meetingId) : pullYouTubeTranscriptLive(meetingId);
}

// ---- Public /meetings --------------------------------------------------------------------------

export function listPublicMeetings(): Promise<PublicMeetingView[]> {
  return USE_MOCKS ? mockDb.listPublicMeetings() : listPublicMeetingsLive();
}
export function getPublicMeeting(id: string): Promise<PublicMeetingDetail | null> {
  return USE_MOCKS ? mockDb.getPublicMeeting(id) : getPublicMeetingLive(id);
}

// ---- Public "Reported Concerns" map (public-concerns-map.md / ADR-0023) -------------------------

export function listPublicConcerns(filter: PublicConcernsFilter = {}): Promise<PublicConcernsView> {
  return USE_MOCKS ? mockDb.listPublicConcerns(filter) : listPublicConcernsLive(filter);
}

// ---- STAFF concerns map (staff-concerns-map.md) -------------------------------------------------

/** EXACT, un-fuzzed, ALL-statuses/categories (incl. staffOnly) counterpart to listPublicConcerns —
 *  Cognito-authed, concerns:read-gated server-side. */
export function listStaffConcernsMap(
  filter: StaffConcernsFilter = {},
): Promise<StaffConcernsView> {
  return USE_MOCKS ? mockDb.listStaffConcernsMap(filter) : listStaffConcernsLive(filter);
}
export type { StaffConcernPin, StaffConcernsFilter, StaffConcernsView } from "./types.js";

// ---- Contributor "My reports" ------------------------------------------------------------------

export function listMyReports(): Promise<MyReportSlot[]> {
  return USE_MOCKS ? mockDb.listMyReports() : listMyReportsLive();
}
export function getMyContribution(
  meetingId: string,
  itemId: string,
): Promise<{
  contribution: ContributorContributionView;
  meeting: ContributorMeetingView;
  item: ContributorAgendaItemView;
} | null> {
  return USE_MOCKS
    ? mockDb.getMyContribution(meetingId, itemId)
    : getMyContributionLive(meetingId, itemId);
}
export function saveMyContribution(
  meetingId: string,
  itemId: string,
  input: SaveContributionInput,
): Promise<ContributorContributionView> {
  return USE_MOCKS
    ? mockDb.saveMyContribution(meetingId, itemId, input)
    : saveMyContributionLive(meetingId, itemId, input);
}
export function submitMyContribution(
  meetingId: string,
  itemId: string,
): Promise<ContributorContributionView> {
  return USE_MOCKS
    ? mockDb.submitMyContribution(meetingId, itemId)
    : submitMyContributionLive(meetingId, itemId);
}
export function attachMyContributionFile(
  meetingId: string,
  itemId: string,
  input: AttachMeetingFileInput,
): Promise<MeetingAttachmentView> {
  return USE_MOCKS
    ? mockDb.attachMyContributionFile(meetingId, itemId, input)
    : attachMyContributionFileLive(meetingId, itemId, input);
}

// ---- Council Meetings historical importer — staff console (meetings-embed-and-import.md §2) ----

import type {
  CommitMeetingImportResult,
  MeetingImportDetail,
  MeetingImportRow,
  StartMeetingImportInput,
  StartMeetingImportResult,
  UpdateMeetingImportRowInput,
} from "./types.js";
import {
  startMeetingImportLive,
  getMeetingImportLive,
  updateMeetingImportRowLive,
  commitMeetingImportLive,
} from "./staffApi.js";
export type {
  CommitMeetingImportResult,
  MeetingImportConfidence,
  MeetingImportCounts,
  MeetingImportDetail,
  MeetingImportFlag,
  MeetingImportLink,
  MeetingImportLinkType,
  MeetingImportRow,
  MeetingImportStatus,
  MeetingImportStoreMode,
  StartMeetingImportInput,
  StartMeetingImportResult,
  UpdateMeetingImportRowInput,
} from "./types.js";
export { meetingImportConfidenceLevel } from "./types.js";

/** POST /api/admin/meetings/import — screen 1 submit. */
export function startMeetingImport(input: StartMeetingImportInput): Promise<StartMeetingImportResult> {
  return USE_MOCKS ? mockDb.startMeetingImport(input) : startMeetingImportLive(input);
}

/** GET /api/admin/meetings/import/{importId} — screen 2 poll target / screen 3 data source. */
export function getMeetingImport(importId: string): Promise<MeetingImportDetail> {
  return USE_MOCKS ? mockDb.getMeetingImport(importId) : getMeetingImportLive(importId);
}

/** PATCH /api/admin/meetings/import/{importId}/rows/{rowId} — review table inline edit / toggle. */
export function updateMeetingImportRow(
  importId: string,
  rowId: string,
  input: UpdateMeetingImportRowInput,
): Promise<MeetingImportRow> {
  return USE_MOCKS
    ? mockDb.updateMeetingImportRow(importId, rowId, input)
    : updateMeetingImportRowLive(importId, rowId, input);
}

/** POST /api/admin/meetings/import/{importId}/commit — the actual write (screen 4 confirm). */
export function commitMeetingImport(importId: string): Promise<CommitMeetingImportResult> {
  return USE_MOCKS ? mockDb.commitMeetingImport(importId) : commitMeetingImportLive(importId);
}

// ---- Ordinance Library — Phase B4 staff console (ordinance-library-phaseB.md §1.3/§1.5) --------

import type {
  OrdinanceCodeVersion,
  OrdinanceDraftIngestResult,
  OrdinancePublishResult,
  OrdinanceReviewProgress,
  OrdinanceVersionDetail,
} from "./types.js";
import {
  uploadOrdinanceFileLive,
  draftIngestOrdinanceLive,
  listOrdinanceVersionsLive,
  getOrdinanceVersionLive,
  markOrdinanceSectionReviewedLive,
  addressOrdinanceWarningLive,
  publishOrdinanceVersionLive,
} from "./staffApi.js";

export type {
  OrdinanceCodeNode,
  OrdinanceCodeVersion,
  OrdinanceCrossRef,
  OrdinanceDraftIngestResult,
  OrdinanceParseNote,
  OrdinancePublishResult,
  OrdinanceReviewProgress,
  OrdinanceVersionDetail,
  OrdinanceVersionStatus,
} from "./types.js";

/** Full upload: presign -> PUT to S3 -> return the s3Key to pass to draftIngestOrdinance. */
export function uploadOrdinanceFile(file: File, onProgress?: (pct: number) => void): Promise<string> {
  return USE_MOCKS
    ? mockDb.uploadOrdinanceFile(file, onProgress)
    : uploadOrdinanceFileLive(file, onProgress);
}

/** Run the format-appropriate extractor and persist a new draft CodeVersion — never publishes. */
export function draftIngestOrdinance(
  input:
    | { s3Key: string; filename: string; contentType: string; label?: string }
    | { citycode: true; label?: string },
): Promise<OrdinanceDraftIngestResult> {
  return USE_MOCKS ? mockDb.draftIngestOrdinance(input) : draftIngestOrdinanceLive(input);
}

export function listOrdinanceVersions(): Promise<OrdinanceCodeVersion[]> {
  return USE_MOCKS ? mockDb.listOrdinanceVersions() : listOrdinanceVersionsLive();
}

/** One version + its full node tree + the SAME publishable/blockedReason read the publish gate
 *  uses — drives the review screen's preview + the "Approve & publish" button state. */
export function getOrdinanceVersion(versionId: string): Promise<OrdinanceVersionDetail> {
  return USE_MOCKS ? mockDb.getOrdinanceVersion(versionId) : getOrdinanceVersionLive(versionId);
}

export function markOrdinanceSectionReviewed(
  versionId: string,
  permalink: string,
): Promise<OrdinanceReviewProgress> {
  return USE_MOCKS
    ? mockDb.markOrdinanceSectionReviewed(versionId, permalink)
    : markOrdinanceSectionReviewedLive(versionId, permalink);
}

export function addressOrdinanceWarning(
  versionId: string,
  warningIndex: number,
): Promise<OrdinanceReviewProgress> {
  return USE_MOCKS
    ? mockDb.addressOrdinanceWarning(versionId, warningIndex)
    : addressOrdinanceWarningLive(versionId, warningIndex);
}

/** THE gate: throws ApiError 409 "not_publishable" unless the version is fully reviewed — server-
 *  enforced (ADR-0014 §1.5), never bypassable from here. */
export function publishOrdinanceVersion(versionId: string): Promise<OrdinancePublishResult> {
  return USE_MOCKS
    ? mockDb.publishOrdinanceVersion(versionId)
    : publishOrdinanceVersionLive(versionId);
}

// ---- Ordinance Library — R3 cross-module reference layer ----------------------------------------

import type {
  OrdinanceRef,
  OrdinanceRefEntityType,
  OrdinanceRefsReverseResult,
  OrdinanceSearchHit,
} from "./types.js";
import {
  attachOrdinanceRefLive,
  detachOrdinanceRefLive,
  listOrdinanceRefsLive,
  getOrdinanceRefsReverseLive,
  searchOrdinanceSectionsLive,
} from "./staffApi.js";

export type {
  OrdinanceRef,
  OrdinanceRefEntityType,
  OrdinanceRefsPublicCounts,
  OrdinanceRefsReverseResult,
  OrdinanceSearchHit,
} from "./types.js";

/** Search/ask the ordinance corpus for a § to attach — reuses the same public "/code" question-
 *  first surface (ordinance_ask.go); always returns the closest cited candidates. */
export function searchOrdinanceSections(query: string): Promise<OrdinanceSearchHit[]> {
  return USE_MOCKS ? mockDb.searchOrdinanceSections(query) : searchOrdinanceSectionsLive(query);
}

/** Attach one § to a Request/FAQ. Throws ApiError 409 "already_attached" / 400
 *  "unknown_permalink" / 400 "validation_error" (over the 20-ref cap) — callers roll back their
 *  optimistic chip-list add on any of these. */
export function attachOrdinanceRef(
  entityType: OrdinanceRefEntityType,
  entityId: string,
  permalink: string,
  sectionLabel: string,
): Promise<OrdinanceRef> {
  return USE_MOCKS
    ? mockDb.attachOrdinanceRef(entityType, entityId, permalink, sectionLabel)
    : attachOrdinanceRefLive(entityType, entityId, permalink, sectionLabel);
}

/** Detach one §. */
export function detachOrdinanceRef(
  entityType: OrdinanceRefEntityType,
  entityId: string,
  permalink: string,
): Promise<void> {
  return USE_MOCKS
    ? mockDb.detachOrdinanceRef(entityType, entityId, permalink).then(() => undefined)
    : detachOrdinanceRefLive(entityType, entityId, permalink);
}

/** One entity's currently-attached §s (chip-list load). */
export function listOrdinanceRefs(
  entityType: OrdinanceRefEntityType,
  entityId: string,
): Promise<OrdinanceRef[]> {
  return USE_MOCKS
    ? mockDb.listOrdinanceRefs(entityType, entityId)
    : listOrdinanceRefsLive(entityType, entityId);
}

/** Staff reverse lookup (has entityId) — the "Cited by" panel next to a live/draft §. */
export function getOrdinanceRefsReverse(permalink: string): Promise<OrdinanceRefsReverseResult> {
  return USE_MOCKS
    ? mockDb.getOrdinanceRefsReverse(permalink)
    : getOrdinanceRefsReverseLive(permalink);
}

// ---- Ordinance Library — Ask-the-code answer rating ("Was this helpful?") ------------------------

import type { OrdinanceRatingsSummary } from "./types.js";
import { getOrdinanceRatingsLive } from "./staffApi.js";

export type { OrdinanceAskDownvote, OrdinanceRatingsSummary } from "./types.js";

/** GET /api/admin/ordinance/ratings — the "Answer quality" panel's overall 👍/👎 tally + most
 *  recent downvoted questions (rated via the public "Was this helpful?" control on the /code
 *  Ask-the-code answer, ordinance-ask.js). */
export function getOrdinanceRatings(): Promise<OrdinanceRatingsSummary> {
  return USE_MOCKS ? mockDb.getOrdinanceRatings() : getOrdinanceRatingsLive();
}

// ---- B&G new-items disclosure gate: staff console (issue #489) --------------------------------

import type { BGShareApprovalView, BGShareCandidateView, SaveBGShareApprovalInput } from "./types.js";

export type { BGShareApprovalView, BGShareCandidateView, BGSharePhotoView, SaveBGShareApprovalInput } from "./types.js";

/** GET /api/staff/bg-share/candidates — every new water concern awaiting (or already carrying) a
 *  clerk's B&G share review. Never carries reporter name/email/phone. */
export function listBGShareCandidates(): Promise<BGShareCandidateView[]> {
  return USE_MOCKS ? mockListBGShareCandidates() : listBGShareCandidatesLive();
}

/** PUT /api/staff/bg-share/{requestId} — save the clerk's redacted description + per-photo
 *  approval and flip the gate to Approved=true. */
export function saveBGShareApproval(
  requestId: string,
  input: SaveBGShareApprovalInput,
): Promise<BGShareApprovalView> {
  return USE_MOCKS
    ? mockSaveBGShareApproval(requestId, input)
    : saveBGShareApprovalLive(requestId, input);
}

/** DELETE /api/staff/bg-share/{requestId} — unapprove; drops the concern back out of the next
 *  export run until a clerk re-reviews it. */
export function unapproveBGShare(requestId: string): Promise<void> {
  return USE_MOCKS ? mockUnapproveBGShare(requestId) : unapproveBGShareLive(requestId);
}

export type { CoachingState, CoachingStateEntry, CoachingStatePatch, CoachingStatus } from "./types.js";
